import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { SortAccessors } from '@/lib/tableSort'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { ColumnCells, ResizableTableHead } from '@/components/table'
import { useSearchParams } from 'react-router-dom'
import { versionLabel } from '@/lib/versioning'
import {
  NO_FORM_HINT,
  processFieldHints,
  processFieldName,
  processFieldRows,
} from '@/lib/processFieldLabels'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { UNMAPPED_FORM, type FormWithProcesses } from '@/api/processStepsApi'
import {
  MAPPING_LABELS,
  processMappingLevel,
  type MappingLevel,
} from '@/lib/processMapping'
import {
  embeddingFamilies,
  formFamiliesWithProcesses,
  formsByProcessField,
  type FormFamilyWithProcesses,
} from '@/lib/processFormUsage'

import { CalendarClock, Check, ChevronDown, CircleDashed, FileText, ListChecks, Loader2, Package, Trash2, Users, Wrench } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { useFieldLibrary, useFieldLibraryList, useFieldUsage } from '@/hooks/useFieldLibrary'
import {
  useDeleteProcessStep,
  useFormsWithProcesses,
  useProcessStepConnection,
  useProcessStepList,
} from '@/hooks/useProcessSteps'
import { EquipmentRequirementTab } from '@/components/process/EquipmentRequirementTab'
import { InventoryRequiredTab } from '@/components/process/InventoryRequiredTab'
import { StaffingRequirementTab } from '@/components/process/StaffingRequirementTab'
import { FieldAttributesTab } from '@/components/process/FieldAttributesTab'

// ─── Process steps ───────────────────────────────────────────────────────────
//
// The five steps a process is composed of. Each is a placeholder for now — the
// step editors land separately (P3b–P7). This page is a read-only shell: it
// picks a Process field, loads its connection + summary, and shows the layout.

type StepValue = 'inventory' | 'staffing' | 'equipment' | 'field-due-date' | 'field-attributes'

interface StepDef {
  value: StepValue
  label: string
  icon: React.ElementType
  /** Which connection child-array (if any) drives this tab's count badge. */
  countKey?: 'inventoryRequirements' | 'equipmentRequirements' | 'staffingLines'
}

const STEPS: StepDef[] = [
  { value: 'inventory', label: 'Inventory', icon: Package, countKey: 'inventoryRequirements' },
  { value: 'staffing', label: 'Staffing', icon: Users, countKey: 'staffingLines' },
  { value: 'equipment', label: 'Equipment', icon: Wrench, countKey: 'equipmentRequirements' },
  { value: 'field-due-date', label: 'Field Due Date', icon: CalendarClock },
  { value: 'field-attributes', label: 'Field Attributes', icon: ListChecks },
]

const DEFAULT_STEP: StepValue = 'inventory'
const VALID_STEPS = new Set<string>(STEPS.map((s) => s.value))

/**
 * "Every form" — the default scope, and a UI concept only.
 *
 * Deliberately not exported alongside `UNMAPPED_FORM` in the API module: that
 * sentinel names a real server-side set (the fields no form embeds), while this
 * one just means "do not filter" and never reaches a request.
 */
const ALL_FORMS = 'all'

/**
 * Mapping-status scope (z8nrz7cra6) — a second axis, independent of the form
 * filter, and the two compose: "not mapped, in the Collection form".
 *
 * Three levels, replacing the binary Configured / Not configured this page
 * shipped with (z8nrz7cr9r). "Configured" answered the wrong question: a step
 * holding one staffing line and nothing else was Configured, and so was
 * indistinguishable from a step that was actually finished — which is exactly the
 * distinction someone auditing a form is looking for. See `processMappingLevel`
 * for what each level means and for the two judgement calls behind it (attributes
 * excluded; a materials-free step cannot reach Fully).
 *
 * **The default view lists everything.** The table used to show only configured
 * processes (except under "Not in any form"); it lists every process in scope and
 * badges the ones that are not fully mapped. Hiding them was defensible while
 * there was nothing to say about them — now there is a level, and "which of my
 * processes still need setting up" is the question this page exists to answer.
 */
const ANY_MAPPING = 'any'

/** A level, or "do not filter". Typed so a mistyped level cannot silently never match. */
type MappingFilter = MappingLevel | typeof ANY_MAPPING

const MAPPING_FILTER_OPTIONS: Array<{ value: MappingFilter; label: string }> = [
  { value: ANY_MAPPING, label: 'Any status' },
  // Ordered as the work progresses, so the list reads as a scale rather than as
  // three unrelated states.
  { value: 'not-mapped', label: MAPPING_LABELS['not-mapped'] },
  { value: 'partially-mapped', label: MAPPING_LABELS['partially-mapped'] },
  { value: 'fully-mapped', label: MAPPING_LABELS['fully-mapped'] },
]

const VALID_MAPPING_FILTERS = new Set(MAPPING_FILTER_OPTIONS.map((o) => o.value))

/**
 * What a `?config=` link from the binary filter means on the new scale.
 *
 * `not-configured` had an exact successor. `configured` did not: it spanned
 * "partially" and "fully" together, and there is no single level that means
 * both — so it widens to "Any status" rather than silently picking one of them
 * and hiding half of what the link used to show.
 */
const LEGACY_CONFIG_FILTERS: Record<string, MappingFilter> = {
  'not-configured': 'not-mapped',
  configured: ANY_MAPPING,
  any: ANY_MAPPING,
}

/** Empty state for a step whose editor hasn't been built yet — Field Due Date. */
function StepPlaceholder({ label }: { label: string }) {
  return (
    <div className='flex flex-col items-center justify-center gap-3 px-6 py-24 text-center'>
      <div className='flex h-12 w-12 items-center justify-center rounded-xl bg-muted'>
        <CircleDashed className='h-6 w-6 text-muted-foreground' />
      </div>
      <p className='text-sm text-muted-foreground'>{label} step is coming soon.</p>
    </div>
  )
}

/**
 * Delete a process that nothing depends on any more.
 *
 * Two independent claims have to hold, and they are checked in different
 * places. "No form uses it" is answered here, by the Field Library usage
 * endpoint, so the button can explain itself before it is pressed. "It has
 * never been run" is answered by the server, which refuses with a 409 — that
 * check cannot live here, because executions can appear between render and
 * click, and because an execution plus its signatures are Part 11 records that
 * only the backend can authoritatively speak for.
 */
function DeleteProcessControl({
  fieldId,
  onDeleted,
}: {
  fieldId: string
  onDeleted: () => void
}) {
  const usageQuery = useFieldUsage(fieldId)
  const removeProcess = useDeleteProcessStep()
  const { remove: removeLibraryField } = useFieldLibrary()
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const usage = usageQuery.data
  const usedBy = usage?.forms ?? []
  const inUse = (usage?.count ?? 0) > 0
  // "Not in use" has to be something we KNOW, not merely something we failed to
  // learn. A usage lookup that errored leaves `count` undefined, and treating
  // that as zero would enable deleting a process other forms still depend on.
  const usageUnknown = !usageQuery.isSuccess
  const blocked = usageUnknown || inUse
  const busy = removeProcess.isPending || removeLibraryField.isPending

  /** 404 = already gone. Anything else (409 for a process that ran) must stop us. */
  const ignoreMissing = (e: unknown) => {
    if (e instanceof Error && /\(404\)/.test(e.message)) return
    throw e
  }

  const handleDelete = async () => {
    setError(null)
    try {
      // Two deletes, no transaction spanning them — so each is made to tolerate
      // having already happened. Without that, a run that removes the
      // connection and then fails on the library row can never be retried: the
      // second attempt dies on the connection's own 404 and the library field
      // is stranded without its configuration forever.
      await removeProcess.mutateAsync({ fieldId }).catch(ignoreMissing)
      await removeLibraryField.mutateAsync(fieldId).catch(ignoreMissing)
      setConfirming(false)
      onDeleted()
    } catch (e) {
      // The server's message names what has run, which is the only thing that
      // tells the user why they cannot proceed.
      setError(e instanceof Error ? e.message : 'Could not delete this process.')
    }
  }

  if (usageQuery.isLoading) return null

  return (
    <div className='mt-3 flex flex-wrap items-center gap-3'>
      {confirming ? (
        <>
          <span className='text-sm text-foreground'>
            Delete this process and its configuration?
          </span>
          <Button variant='outline' size='sm' disabled={busy} onClick={() => setConfirming(false)}>
            Cancel
          </Button>
          <Button variant='destructive' size='sm' disabled={busy} onClick={handleDelete}>
            {busy ? 'Deleting…' : 'Delete process'}
          </Button>
        </>
      ) : (
        <Button
          variant='outline'
          size='sm'
          disabled={blocked}
          title={
            usageUnknown
              ? 'Cannot check which forms use this process'
              : inUse
                ? `In use by ${usedBy.map((f) => f.name).join(', ')}`
                : 'Delete this process and its configuration'
          }
          onClick={() => setConfirming(true)}
          className={cn(!blocked && 'text-red-600 hover:bg-red-50 hover:text-red-700')}
        >
          <Trash2 className='mr-1.5 h-4 w-4' />
          Delete process
        </Button>
      )}
      {inUse && !confirming && (
        <p className='text-xs text-muted-foreground'>
          Used by {usedBy.length} form{usedBy.length === 1 ? '' : 's'} — remove it there first.
        </p>
      )}
      {usageUnknown && !confirming && (
        <p className='text-xs text-muted-foreground'>
          Could not check which forms use this process, so deleting is held back.
        </p>
      )}
      {error && <p className='text-xs font-medium text-red-600'>{error}</p>}
    </div>
  )
}

/**
 * The Forms cell: how many forms embed this process, and which (z8nrz7cnj6).
 *
 * A popover rather than an inline list. Process config is keyed on the LIBRARY
 * field, so a single process is routinely embedded by several forms — inline
 * names made the tallest row set the height of every row beside it.
 *
 * The trigger stops propagation because the ROW is itself clickable (it selects
 * the process for configuration). Without that, opening the list also navigates
 * away from it, which reads as the popover refusing to open.
 *
 * Zero is rendered as plain muted text with no trigger: there is nothing to
 * open, and a clickable control that opens an empty panel is worse than none.
 * That state is meaningful here — a process no form embeds is exactly the
 * orphan the "Not in any form" filter exists to find.
 *
 * **Counts FORMS, not definitions.** A process embedded in Collection V2 and V3
 * used to read "2", which is one form counted twice — and this column exists to
 * answer how many forms share the bill of materials, where the answer is one.
 * The versions are not thrown away: a row says the newest one, and `+N` when
 * earlier versions hold it too.
 */
function ProcessFormsCell({ forms }: { forms: FormWithProcesses[] }) {
  const [open, setOpen] = useState(false)
  const families = embeddingFamilies(forms)
  if (families.length === 0) {
    return <span className='text-muted-foreground'>0</span>
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          onClick={(e) => e.stopPropagation()}
          className='inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium text-primary hover:bg-primary/10'
          aria-label={`${families.length} form${families.length === 1 ? '' : 's'} use this process`}
        >
          {families.length}
          <ChevronDown className='h-3 w-3' />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align='end'
        className='w-64 p-2'
        onClick={(e) => e.stopPropagation()}
      >
        <p className='px-1 pb-1 text-xs font-medium text-muted-foreground'>Used by</p>
        <ul className='max-h-56 space-y-0.5 overflow-y-auto'>
          {families.map((family) => (
            <li
              key={family.familyId}
              className='flex items-center justify-between gap-2 rounded px-1 py-1 text-sm'
            >
              <span className='truncate'>{family.name}</span>
              {/* The newest version embedding it, and how many earlier ones do
                  as well. Shared helper, not a hand-rolled `v3`, so it matches
                  every other version on screen. */}
              <span className='shrink-0 text-xs tabular-nums text-muted-foreground'>
                {versionLabel(family.version)}
                {family.versionCount > 1 ? ` +${family.versionCount - 1}` : ''}
              </span>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  )
}

/**
 * Searchable Process-field picker — debounced search over process-type library
 * fields.
 *
 * **Search only. It deliberately cannot create.**
 *
 * It used to offer "create <typed name> as a Process field" when the query had
 * no exact match, and that was this page's orphan factory. A field created here
 * is born belonging to no form and nothing ever attaches it to one: type a
 * name, walk away, and the library keeps a process that no form embeds, forever.
 * Those accumulate as the abandoned rows — "cancelled delete" and friends — that
 * made the "Not in any form" bucket the noisiest thing on the screen.
 *
 * It also contradicted the model the rest of the code states plainly: a process
 * joins a form on its CANVAS, where insertion copies it in with a real
 * `libraryFieldId` binding it to that form. This page configures what an
 * existing process contributes; it is not where processes come from.
 */
function ProcessFieldPicker({
  fieldId,
  onSelect,
  allowsField,
  formsByFieldId,
}: {
  fieldId: string | null
  onSelect: (id: string) => void
  /** Restricts the offered fields to the form currently filtered on. */
  allowsField: (id: string) => boolean
  /** Which forms embed each process — the material for disambiguating names. */
  formsByFieldId: Map<string, FormWithProcesses[]>
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 250)
    return () => clearTimeout(t)
  }, [query])

  const listQuery = useFieldLibraryList({
    fieldType: 'process',
    q: debouncedQuery || undefined,
    // Only offer enabled fields for selection here; the label-resolution
    // fallback below intentionally stays unfiltered so an already-selected
    // field that's since been disabled still shows its name.
    enabled: true,
    // e2e debris is not a process anyone means to configure. See
    // `FieldLibraryListParams.isTestData` — an e2e run still sees its own rows.
    isTestData: false,
    take: 50,
  })
  // The form filter decides which processes exist for this view; the search box
  // narrows within them.
  const fields = (listQuery.data?.items ?? []).filter((f) => allowsField(f.id))

  /**
   * Qualifiers for same-named entries (z8nrz7cr9r).
   *
   * Computed over the offered list, not the table's — this picker draws from its
   * own `useFieldLibraryList` query and can show processes the table's status
   * filter excludes, so the two lists genuinely need their own answers to "which
   * of these is which".
   */
  const hints = processFieldHints(fields, formsByFieldId)

  /**
   * The offered list, expanded to one row per embedding form (z8nrz7cr9r).
   *
   * More rows than fields means at least one process sits in several forms — the
   * case the note below exists for, since several rows selecting one shared
   * configuration is the opposite of what the layout suggests.
   */
  const rows = processFieldRows(fields, formsByFieldId)
  const sharedAcrossForms = rows.length > fields.length

  // Resolve the current label for the trigger — search the loaded page, else fall
  // back to a dedicated lookup so a previously-selected field still shows a name.
  const selectedInList = fields.find((f) => f.id === fieldId)
  const selectedLookup = useFieldLibraryList(
    // NOT filtered on isTestData: this only resolves the NAME of an
    // already-selected field, and a field selected inside an e2e run must still
    // render its label rather than going blank.
    fieldId && !selectedInList ? { fieldType: 'process', take: 100 } : {},
  )
  const selected =
    selectedInList ?? selectedLookup.data?.items.find((f) => f.id === fieldId) ?? null

  const trimmed = query.trim()

  const select = (id: string) => {
    onSelect(id)
    setOpen(false)
    setQuery('')
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          id='process-field'
          aria-expanded={open}
          className={cn(
            'flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-left text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring',
            !selected && 'text-muted-foreground',
          )}
        >
          <span className='min-w-0 flex-1 truncate'>
            {selected ? processFieldName(selected) : 'Choose a Process field…'}
          </span>
          {/* The qualifier rides on the trigger too. Without it the control
              confirms only the name, which is the half that was ambiguous.
              Same width rule as the list rows: capped and shrinkable, so the
              name is never the part that gets an ellipsis. */}
          {selected && hints.get(selected.id) && (
            <span className='min-w-0 max-w-[45%] truncate text-xs text-muted-foreground'>
              {hints.get(selected.id)}
            </span>
          )}
          <CircleDashed className='h-4 w-4 shrink-0 opacity-50' />
        </button>
      </PopoverTrigger>
      <PopoverContent className='w-[--radix-popover-trigger-width] p-0' align='start'>
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder='Search Process fields…'
          className='w-full border-b bg-transparent px-3 py-2 text-sm outline-none placeholder:text-muted-foreground'
        />
        {/* Stated before a choice is made, not after. Listing a process once per
            form reads as though each row were separately configurable, and it is
            not: config is keyed on the library field, so all of a process's rows
            open the same bill of materials. Without this the layout would quietly
            invite the mistake the Forms column was added to prevent. */}
        {sharedAcrossForms && (
          <p className='border-b bg-muted/40 px-3 py-1.5 text-[11px] leading-snug text-muted-foreground'>
            A process used by several forms is listed once per form. All of its rows
            configure the same process.
          </p>
        )}
        <div className='max-h-64 overflow-y-auto p-1'>
          {listQuery.isLoading ? (
            <div className='flex items-center justify-center py-6'>
              <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
            </div>
          ) : (
            <>
              {/* One row per embedding form, laid out like the table's "Used by"
                  popover — name, then the form, version right-aligned — so the
                  two surfaces read the same way. */}
              {rows.map((row) => (
                <button
                  key={row.key}
                  type='button'
                  onClick={() => select(row.fieldId)}
                  className='flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-accent'
                >
                  <Check
                    className={cn(
                      'h-3.5 w-3.5 shrink-0',
                      row.fieldId === fieldId ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className='min-w-0 flex-1 truncate'>{row.name}</span>
                  {row.form ? (
                    <>
                      {/* Shrinkable and capped: the process name is the primary
                          information and must never be the part that ellipses. */}
                      <span className='min-w-0 max-w-[45%] truncate text-xs text-muted-foreground'>
                        {row.form.name}
                      </span>
                      <span className='shrink-0 text-xs tabular-nums text-muted-foreground'>
                        {versionLabel(row.form.version)}
                      </span>
                    </>
                  ) : (
                    <span className='shrink-0 text-xs italic text-muted-foreground'>
                      {NO_FORM_HINT}
                    </span>
                  )}
                </button>
              ))}
              {rows.length === 0 && (
                <p className='px-2 py-3 text-center text-xs text-muted-foreground'>
                  {trimmed
                    ? `No Process field matches “${trimmed}”.`
                    : 'No Process fields in this form.'}
                  <br />
                  <span className='text-muted-foreground/70'>
                    Add a Process field on the form’s canvas to create one.
                  </span>
                </p>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

// Per-user column layout for the process list. Process and the Configure cell
// are locked. See src/components/table/index.ts for the pattern.
const PROCESS_TABLE_ID = 'process-list'

const PROCESS_COLUMNS: TableColumnDef[] = [
  { id: 'process', header: 'Process', locked: true, sortable: true },
  { id: 'equipment', header: 'Equipment', sortable: true },
  { id: 'inventory', header: 'Inventory', sortable: true },
  { id: 'staffing', header: 'Staffing', sortable: true },
  { id: 'attributes', header: 'Attributes', sortable: true },
  { id: 'forms', header: 'Forms', sortable: true },
  { id: 'actions', header: 'Configure', locked: true },
]

/*
 * Widths are declared and sum to 100%. Left to itself the Process column
 * absorbed every pixel of slack — about half the table for a name a third that
 * wide — which stranded the four counts against the right edge behind a large
 * empty gap. The numeric columns are equal so they read as one group.
 *
 * A width the user drags overrides these inline (see ResizableTableHead), which
 * is the point: the percentages are a good default, not a verdict.
 */
const PROCESS_HEAD_CLASSES: Record<string, string> = {
  process: 'w-[28%]',
  equipment: 'w-[12%] text-right',
  inventory: 'w-[12%] text-right',
  staffing: 'w-[12%] text-right',
  attributes: 'w-[12%] text-right',
  forms: 'w-[12%] text-right',
  actions: 'w-[12%]',
}

// Each header's icon sits beside its word — the same icon the matching step
// carries in STEPS. Kept as content here so `label` can stay the plain name.
const PROCESS_HEAD_CONTENT: Record<string, ReactNode> = {
  process: 'Process',
  equipment: (
    <span className='inline-flex items-center gap-1'>
      <Wrench className='h-3.5 w-3.5' /> Equipment
    </span>
  ),
  inventory: (
    <span className='inline-flex items-center gap-1'>
      <Package className='h-3.5 w-3.5' /> Inventory
    </span>
  ),
  staffing: (
    <span className='inline-flex items-center gap-1'>
      <Users className='h-3.5 w-3.5' /> Staffing
    </span>
  ),
  attributes: (
    <span className='inline-flex items-center gap-1'>
      <ListChecks className='h-3.5 w-3.5' /> Attributes
    </span>
  ),
  forms: (
    <span className='inline-flex items-center gap-1'>
      <FileText className='h-3.5 w-3.5' /> Forms
    </span>
  ),
  actions: null,
}

export function ProcessPage() {
  const cols = useTableColumns(PROCESS_TABLE_ID, PROCESS_COLUMNS)
  // The parent Form Builder owns ?tab=, so the step + selected field ride on
  // their own params and can't collide: /form-builder?tab=process&field=…&step=…
  const [params, setParams] = useSearchParams()

  const requested = params.get('step')
  const step: StepValue =
    requested && VALID_STEPS.has(requested) ? (requested as StepValue) : DEFAULT_STEP

  const fieldId = params.get('field')

  const connectionQuery = useProcessStepConnection(fieldId)
  const connection = connectionQuery.data ?? null

  // Default view (no field selected) lists the created Process fields so the user
  // can pick one — or create a new one via the picker above.
  const processFieldsQuery = useFieldLibraryList({
    fieldType: 'process',
    isTestData: false,
    take: 100,
  })
  const processFields = processFieldsQuery.data?.items ?? []

  // The form scopes BOTH the picker and the table below it. It used to narrow
  // only the pickers, on the reasoning that the table should stay a whole
  // roster — but that left the two halves of one screen disagreeing: the form
  // said "Collection", the picker offered Collection's processes, and the table
  // underneath still listed every process in the tenant. The form is the
  // question the page is being asked; everything under it answers that question.
  //
  // Lives in the query string beside `step`/`field` so a reload, a bookmark and
  // a shared link all land on the same selection.
  //
  // The default is ALL_FORMS, not UNMAPPED_FORM. Defaulting to "Not in any
  // form" opened the page on the complement — process fields that no form
  // embeds, which in practice is abandoned and half-deleted library rows — so
  // the first thing the picker offered was the junk and none of the real work.
  //
  // Read RAW here: the filter now holds a form FAMILY, but a link made before
  // that carries a definition id. `formFilter` below resolves one to the other,
  // so an old bookmark lands on the same form instead of reading as a form that
  // no longer exists.
  const requestedForm = params.get('form') ?? ALL_FORMS
  // Sanitised because it arrives from the URL: an unrecognised value would
  // otherwise filter the table down to nothing with no way to tell why.
  //
  // `?mapping=` is the param, because that is what the axis measures now. A
  // `?config=` link from the binary filter is translated rather than dropped —
  // see LEGACY_CONFIG_FILTERS — so a shared link keeps meaning something.
  const requestedMapping = params.get('mapping') ?? LEGACY_CONFIG_FILTERS[params.get('config') ?? '']
  const mappingFilter: MappingFilter =
    requestedMapping && VALID_MAPPING_FILTERS.has(requestedMapping as MappingFilter)
      ? (requestedMapping as MappingFilter)
      : ANY_MAPPING
  const processStepList = useProcessStepList()

  // Only forms that actually contain a Process field. Listing every form would
  // make most of the dropdown dead options that resolve to an empty table.
  const formsQuery = useFormsWithProcesses()
  const formsWithProcesses: FormWithProcesses[] = useMemo(
    () => formsQuery.data ?? [],
    [formsQuery.data],
  )

  /**
   * The forms to offer — one per family, not one per version.
   *
   * The endpoint returns a row per definition, so a form with four versions
   * arrived as four options reading "kit creation form · V1" … "· V4" with
   * nothing to choose between them. That is the duplication in this filter, and
   * it is not duplicate data: it is one form listed once per version.
   */
  const formFamilies: FormFamilyWithProcesses[] = useMemo(
    () => formFamiliesWithProcesses(formsWithProcesses),
    [formsWithProcesses],
  )

  // Definition id → its family, so a pre-collapse link still resolves.
  const familyIdByFormId = useMemo(
    () => new Map(formsWithProcesses.map(f => [f.id, f.familyId] as const)),
    [formsWithProcesses],
  )

  // Sanitised in the effect below, not here: an unknown value has to survive
  // until the forms have actually loaded, or a slow fetch clears a valid filter.
  const formFilter = familyIdByFormId.get(requestedForm) ?? requestedForm

  const formFilterOptions = useMemo(
    () => [
      { value: ALL_FORMS, label: 'All forms' },
      // Valued on familyId — the lineage, which is what one option now stands
      // for. The version still shows, because it says WHICH version the form is
      // on today, but it no longer multiplies the entries.
      ...formFamilies.map(f => ({
        value: f.familyId,
        label: `${f.name} · ${versionLabel(f.version)}`,
      })),
      // Last, not first. This is the exception case — processes belonging to no
      // form, which is how you find an orphan to clean up — and leading with it
      // made the page open on the one view nobody wants by default.
      { value: UNMAPPED_FORM, label: 'Not in any form' },
    ],
    [formFamilies],
  )

  /**
   * Which process fields are in scope under the current form — used by BOTH the
   * picker and the table, so the two can never disagree.
   *
   * Derived from the form map rather than the connection list, because the
   * picker deliberately offers process fields that have no connection yet —
   * that is how a new one gets configured — and those have no row to filter on.
   */
  const allowsField = useMemo(() => {
    if (formFilter === ALL_FORMS) return () => true
    if (formFilter !== UNMAPPED_FORM) {
      // Every process ANY version of the family embeds — see
      // `formFamiliesWithProcesses` for why the union and not just the newest
      // version: a process dropped from V4 is still Collection's process, and
      // scoping to V4 alone would show it under "Not in any form".
      const ids = new Set(formFamilies.find(f => f.familyId === formFilter)?.fieldIds ?? [])
      return (id: string) => ids.has(id)
    }
    // "Not in any form" is the complement: everything no form embeds, which
    // includes the not-yet-configured fields living purely in the library.
    const embedded = new Set(formFamilies.flatMap(f => f.fieldIds))
    return (id: string) => !embedded.has(id)
  }, [formFilter, formFamilies])
  /**
   * fieldId → its mapping level (z8nrz7cra6).
   *
   * Built from the same counts the table draws, through the shared rule, so the
   * badge on a row, the level the filter selects on and the numbers beside them
   * can never tell three different stories. A field with no connection is absent
   * from the map and resolves to `not-mapped`.
   */
  const mappingByFieldId = new Map<string, MappingLevel>(
    (processStepList.data ?? []).map(
      (c) => [c.fieldId, processMappingLevel(c._count)] as const,
    ),
  )
  const mappingOf = (id: string): MappingLevel =>
    mappingByFieldId.get(id) ?? processMappingLevel(undefined)

  /**
   * Does a field match the mapping-status filter? (z8nrz7cra6)
   *
   * Split out from the form scope on purpose: the two axes are independent, so
   * "not mapped" answers the same question inside one form as it does across the
   * tenant.
   */
  const matchesMappingFilter = (id: string): boolean =>
    mappingFilter === ANY_MAPPING || mappingOf(id) === mappingFilter

  // Scoped by the same `allowsField` the picker uses, so the table under a form
  // is that form's processes and nothing else — the two controls cannot show
  // different answers to the same question.
  //
  // Mapping status is a filter rather than a hidden rule. This used to list only
  // configured processes (except under "Not in any form", which listed everything
  // so orphans could be found), so an unmapped process was invisible here and
  // reachable only through the picker. It is listed and badged instead — see
  // ANY_MAPPING for why that trade flipped.
  // Kept separate from the status filter below so the empty state can tell
  // "this scope holds nothing" apart from "the status filter excluded
  // everything" — the two need opposite messages.
  const scopedProcessFields = processFields.filter((f) => allowsField(f.id))
  const listedProcessFields = scopedProcessFields.filter((f) => matchesMappingFilter(f.id))
  /**
   * fieldId → the numbers the table draws.
   *
   * `attributeCount` rides with the child-row counts rather than in a second map
   * because it is the same kind of thing: one more dimension of the process's
   * configuration, summarised server-side. A process with no connection at all
   * has no entry, and the row falls back to zeroes below.
   */
  const countsByFieldId = new Map(
    (processStepList.data ?? []).map(
      (c) => [c.fieldId, { ...c._count, attributeCount: c.attributeCount }] as const,
    ),
  )
  /**
   * Which forms embed each process (z8nrz7cnj6) — inverted from the form map
   * this page ALREADY loads for the filter above, so the column and the filter
   * cannot give different answers. See `processFormUsage` for why the count is
   * worth showing: process config is keyed on the library field, so forms
   * sharing a process share one bill of materials.
   */
  const formsByFieldId = useMemo(() => formsByProcessField(formsWithProcesses), [formsWithProcesses])

  const setStep = (next: string) => {
    setParams(
      (prev) => {
        prev.set('step', next)
        return prev
      },
      { replace: true },
    )
  }

  const setField = (next: string) => {
    setParams(
      (prev) => {
        prev.set('field', next)
        return prev
      },
      { replace: true },
    )
  }

  // A form id survives a tenant switch in the URL, but the form itself does not
  // cross tenants — the filter would then return nothing and read as "this form
  // has no processes" rather than "that form is not yours". Fall back once the
  // list has actually loaded, so a slow fetch never looks like a missing form.
  useEffect(() => {
    if (!formsQuery.isSuccess) return
    // The two sentinels are not form ids and outlive any tenant.
    if (formFilter === UNMAPPED_FORM || formFilter === ALL_FORMS) return
    if (formFilterOptions.some(o => o.value === formFilter)) {
      // A link written before the filter collapsed versions names a definition.
      // It resolved, so the filter is correct — rewrite the param to the family
      // so the URL matches the control and stays stable when the form is
      // re-versioned. The selected field is untouched: it is still in scope.
      if (requestedForm !== formFilter) {
        setParams(
          (prev) => {
            prev.set('form', formFilter)
            return prev
          },
          { replace: true },
        )
      }
      return
    }
    setParams(
      (prev) => {
        prev.delete('form')
        prev.delete('field')
        return prev
      },
      { replace: true },
    )
  }, [formsQuery.isSuccess, requestedForm, formFilter, formFilterOptions, setParams])

  const setFormFilter = (next: string) => {
    setParams(
      (prev) => {
        prev.set('form', next)
        // The selected field belongs to the list being replaced, so keeping it
        // would leave the page showing a process the new filter excludes.
        prev.delete('field')
        return prev
      },
      { replace: true },
    )
  }

  const setMappingFilter = (next: string) => {
    setParams(
      (prev) => {
        prev.set('mapping', next)
        // The binary param would otherwise sit in the URL contradicting the new
        // one, and it wins on the next read only if `mapping` is absent — so
        // clear it rather than leave two answers in one link.
        prev.delete('config')
        // Same reasoning as the form filter: a selected process may not survive
        // the narrowing, and a detail pane for an excluded row reads as a bug.
        prev.delete('field')
        return prev
      },
      { replace: true },
    )
  }

  /**
   * Qualifiers for same-named processes in the TABLE (z8nrz7cr9r).
   *
   * Computed over the rows actually listed, so the hint answers "which of these
   * is which" — comparing against processes the current filters exclude would
   * qualify a name that reads as unique on screen.
   */
  // Not memoised: `listedProcessFields` is a fresh array every render (as are
  // `mappingByFieldId` and the filter above it), so a dependency on it would
  // never hit and would only imply caching that isn't happening. The list is
  // tens of rows.
  // Header sorting (z8nrz7e4h6). The counts live in the connection list and
  // the form map, not on the field, so the accessors are built here. A process
  // with no connection draws zeroes, and sorts as zero.
  const processSort = useMemo<SortAccessors<(typeof processFields)[number]>>(() => {
    const counts = new Map(
      (processStepList.data ?? []).map(
        (c) => [c.fieldId, { ...c._count, attributeCount: c.attributeCount }] as const,
      ),
    )
    return {
      process: (f) => processFieldName(f),
      equipment: (f) => counts.get(f.id)?.equipmentRequirements ?? 0,
      inventory: (f) => counts.get(f.id)?.inventoryRequirements ?? 0,
      staffing: (f) => counts.get(f.id)?.staffingLines ?? 0,
      attributes: (f) => counts.get(f.id)?.attributeCount ?? 0,
      forms: (f) => embeddingFamilies(formsByFieldId.get(f.id) ?? []).length,
    }
  }, [processStepList.data, formsByFieldId])
  const sortedProcessFields = useSortedRows(listedProcessFields, cols.sort, processSort)
  const tableHints = processFieldHints(listedProcessFields, formsByFieldId)

  /**
   * What the table below is currently showing, in the user's own terms.
   *
   * Composed from both filters rather than branching on the form alone: with
   * mapping status now a visible axis, a caption that mentioned only the form
   * would claim more than the table shows.
   */
  const scopeLabel = useMemo(() => {
    const what =
      mappingFilter === 'fully-mapped'
        ? 'Fully mapped processes'
        : mappingFilter === 'partially-mapped'
          ? 'Part-mapped processes — some of equipment, materials and staffing'
          : mappingFilter === 'not-mapped'
            ? 'Processes with nothing mapped yet'
            : 'Every process'

    const where =
      formFilter === ALL_FORMS
        ? 'in this tenant'
        : formFilter === UNMAPPED_FORM
          ? 'that no form embeds'
          : `in ${formFilterOptions.find(o => o.value === formFilter)?.label ?? 'this form'}`

    // The orphan view is the one people arrive at to clean up, so it keeps its
    // nudge about what to do next.
    const orphanNote =
      formFilter === UNMAPPED_FORM ? ' Open one to delete it if it is no longer wanted.' : ''

    return `${what} ${where}.${orphanNote}`
  }, [mappingFilter, formFilter, formFilterOptions])

  const countFor = (key: StepDef['countKey']): number =>
    key ? (connection?.[key]?.length ?? 0) : 0

  return (
    <div className='px-6 py-6'>
      <h2 className='text-sm font-semibold text-foreground'>Process steps</h2>
      <p className='mt-0.5 text-sm text-muted-foreground'>
        Configure what each step of a process contributes.
      </p>

      {/* Form first, then process: the form SCOPES both the picker beside it and
          the table below, so reading left to right matches the order the choices
          actually apply. */}
      <div className='mt-4 flex flex-wrap items-end gap-4'>
        <div className='w-full max-w-sm'>
          <label className='mb-1 block text-xs font-medium text-muted-foreground'>Form</label>
          <SearchableSelect
            value={formFilter}
            onChange={setFormFilter}
            options={formFilterOptions}
            placeholder='All forms'
            emptyMessage='No forms exist yet.'
          />
        </div>
        {/* Between form and process, matching the order the choices apply: the
            form scopes, status narrows within it, then a process is picked. */}
        <div className='w-full max-w-[12rem]'>
          <label className='mb-1 block text-xs font-medium text-muted-foreground'>Status</label>
          <SearchableSelect
            value={mappingFilter}
            onChange={setMappingFilter}
            options={MAPPING_FILTER_OPTIONS}
            placeholder='Any status'
          />
        </div>
        <div className='w-full max-w-sm'>
          <label
            className='mb-1 block text-xs font-medium text-muted-foreground'
            htmlFor='process-field'
          >
            Process
          </label>
          {/* Both filters, not just the form one. setMappingFilter drops the
              selected field because it may not survive the narrowing — but with
              only `allowsField` here the picker still offered the excluded rows,
              so the very next click put the page back in the state that clear
              existed to prevent. */}
          <ProcessFieldPicker
            fieldId={fieldId}
            onSelect={setField}
            allowsField={(id) => allowsField(id) && matchesMappingFilter(id)}
            formsByFieldId={formsByFieldId}
          />
        </div>
      </div>

      {!fieldId ? (
        <div className='mt-6'>
          {/* The table is scoped, so it says what it is scoped to. A bare
              "Processes" over a filtered list is how someone concludes a
              process was deleted when it is simply on another form. */}
          <div className='mb-3'>
            <h3 className='text-sm font-semibold text-foreground'>Processes</h3>
            <p className='mt-0.5 text-xs text-muted-foreground'>{scopeLabel}</p>
          </div>
          {processFieldsQuery.isLoading || processStepList.isLoading ? (
            <div className='flex items-center justify-center rounded-xl border border-gray-200 bg-white py-16 shadow-sm'>
              <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
            </div>
          ) : listedProcessFields.length === 0 ? (
            <div className='rounded-xl border border-gray-200 bg-white shadow-sm'>
              <div className='flex flex-col items-center justify-center gap-3 px-6 py-24 text-center'>
                <div className='flex h-12 w-12 items-center justify-center rounded-xl bg-muted'>
                  <CircleDashed className='h-6 w-6 text-muted-foreground' />
                </div>
                <p className='max-w-md text-sm text-muted-foreground'>
                  {/* The form scope is checked first, because a scope holding
                      no processes at all cannot be a status result: saying
                      "every process here is fully mapped" of an empty form
                      states something about processes that do not exist. Only
                      once the scope has something in it does the status filter
                      become the likely reason for an empty table — and then it
                      is the more useful thing to blame, since pointing at the
                      form filter would send someone looking in the wrong
                      place. */}
                  {scopedProcessFields.length === 0
                    ? formFilter === UNMAPPED_FORM
                      ? 'No process belongs to no form — nothing to clean up here.'
                      : formFilter === ALL_FORMS
                        ? 'No processes yet. Add a Process field on a form’s canvas, then pick it above to configure what it contributes.'
                        : 'This form has no processes yet. Add a Process field on its canvas, or switch to All forms to see the rest.'
                    : mappingFilter === 'not-mapped'
                      ? 'Every process in this scope has something mapped — nothing is waiting to be started.'
                      : mappingFilter === 'fully-mapped'
                        ? 'No process here is fully mapped yet. Switch Status to “Partially Mapped” to see how far the rest have got.'
                        : mappingFilter === 'partially-mapped'
                          ? 'No process here is part-way through — each one is either untouched or finished.'
                          : 'No process matches this status.'}
                </p>
              </div>
            </div>
          ) : (
            <>
            <div className='overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm'>
              <Table>
                {/* Widths are declared and sum to 100%. Left to itself the
                    Process column absorbed every pixel of slack — about half the
                    table for a name a third that wide — which stranded the four
                    counts against the right edge behind a large empty gap. The
                    numeric columns are equal so they read as one group. */}
                {/* Header CONTENT comes from PROCESS_HEAD_CONTENT rather than
                    from `def.header`, because these headers carry an icon beside
                    the word — the same icon the matching step carries in STEPS,
                    so a column and the tab it summarises read as one thing.
                    `label` still carries the plain name, which is what fixes the
                    cell's accessible name (see ResizableTableHead). */}
                <TableHeader>
                  <TableRow>
                    {cols.visible.map(({ def }) => (
                      <ResizableTableHead
                        key={def.id}
                        columnId={def.id}
                        columns={cols}
                        label={def.header}
                        className={PROCESS_HEAD_CLASSES[def.id]}
                        resizable={def.id !== 'actions'}
                      >
                        {PROCESS_HEAD_CONTENT[def.id]}
                      </ResizableTableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedProcessFields.map((field) => {
                    const counts = countsByFieldId.get(field.id) ?? {
                      inventoryRequirements: 0,
                      equipmentRequirements: 0,
                      staffingLines: 0,
                      attributeCount: 0,
                    }
                    return (
                      <TableRow
                        key={field.id}
                        onClick={() => setField(field.id)}
                        className='cursor-pointer'
                      >
                        <ColumnCells
                          columns={cols}
                          cells={{
                            process: (
                              <TableCell>
                                {/* `flex w-full min-w-0`, not `inline-flex`: an
                                    inline-flex has no width to be constrained by, so
                                    the name never truncated and the cell simply grew,
                                    overriding the column width declared above. */}
                                <span className='flex w-full min-w-0 items-center gap-2.5 font-medium'>
                                  <span className='flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10'>
                                    <CircleDashed className='h-4 w-4 text-primary' />
                                  </span>
                                  <span className='flex min-w-0 flex-1 flex-col'>
                                    <span className='truncate'>{processFieldName(field)}</span>
                                    {/* Same rule as the picker: only a name that
                                        collides with another visible row is qualified. */}
                                    {tableHints.get(field.id) && (
                                      <span className='truncate text-xs font-normal text-muted-foreground'>
                                        {tableHints.get(field.id)}
                                      </span>
                                    )}
                                  </span>
                                  {/* The whole point of the status filter, said on the
                                      row: a process contributing nothing to a run, and
                                      one only part-way set up, both read as a row of
                                      numbers that does not say whether that is
                                      deliberate or unfinished.

                                      Fully mapped gets no badge. It is the state that
                                      needs no action, and badging every row would
                                      spend the eye's attention on the rows that are
                                      already done. */}
                                  {mappingOf(field.id) === 'not-mapped' ? (
                                    <span className='shrink-0 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700'>
                                      {MAPPING_LABELS['not-mapped']}
                                    </span>
                                  ) : mappingOf(field.id) === 'partially-mapped' ? (
                                    <span className='shrink-0 rounded-full border border-slate-300 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-600'>
                                      {MAPPING_LABELS['partially-mapped']}
                                    </span>
                                  ) : null}
                                </span>
                              </TableCell>
                            ),
                            equipment: (
                              <TableCell className='text-right tabular-nums'>
                                {counts.equipmentRequirements}
                              </TableCell>
                            ),
                            inventory: (
                              <TableCell className='text-right tabular-nums'>
                                {counts.inventoryRequirements}
                              </TableCell>
                            ),
                            staffing: (
                              <TableCell className='text-right tabular-nums'>
                                {counts.staffingLines}
                              </TableCell>
                            ),
                            // The step's attributes: its METHOD, plus the free-form
                            // `attributes` map the runtime shows as `key: value` chips.
                            // Method is counted once however many methods cover the
                            // step — it is one attribute, not one per method. Plain
                            // zero, not a dash: a dash on every row would read as
                            // "not applicable" when it means "none set".
                            attributes: (
                              <TableCell className='text-right tabular-nums'>
                                {counts.attributeCount}
                              </TableCell>
                            ),
                            forms: (
                              <TableCell className='text-right tabular-nums'>
                                <ProcessFormsCell forms={formsByFieldId.get(field.id) ?? []} />
                              </TableCell>
                            ),
                            actions: (
                              <TableCell className='text-right'>
                                <span className='text-xs font-medium text-primary'>Configure →</span>
                              </TableCell>
                            ),
                          }}
                        />
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
            </>
          )}
        </div>
      ) : (
        <>
          <DeleteProcessControl fieldId={fieldId} onDeleted={() => setField('')} />

          <Tabs value={step} onValueChange={setStep} className='mt-4'>
            <TabsList>
              {STEPS.map(({ value, label, icon: Icon, countKey }) => (
                <TabsTrigger key={value} value={value} className='gap-2'>
                  <Icon className='h-4 w-4' />
                  {label}
                  {countKey ? (
                    <Badge variant='secondary' className='ml-1'>
                      {countFor(countKey)}
                    </Badge>
                  ) : null}
                </TabsTrigger>
              ))}
            </TabsList>

            {STEPS.map(({ value, label }) => {
              // Keep the editor tabs mounted across step switches so unsaved
              // draft rows survive Inventory ↔ Equipment hops (Radix unmounts
              // inactive tab content by default).
              const isEditor =
                value === 'equipment' || value === 'inventory' || value === 'staffing'
              return (
                <TabsContent
                  key={value}
                  value={value}
                  forceMount={isEditor ? true : undefined}
                  className={cn('mt-4', isEditor && 'data-[state=inactive]:hidden')}
                >
                  <div className='rounded-xl border border-gray-200 bg-white shadow-sm'>
                    {value === 'equipment' ? (
                      <EquipmentRequirementTab fieldId={fieldId} connection={connection} />
                    ) : value === 'inventory' ? (
                      <InventoryRequiredTab fieldId={fieldId} connection={connection} />
                    ) : value === 'staffing' ? (
                      <StaffingRequirementTab fieldId={fieldId} connection={connection} />
                    ) : value === 'field-attributes' ? (
                      <FieldAttributesTab fieldId={fieldId} />
                    ) : (
                      <StepPlaceholder label={label} />
                    )}
                  </div>
                </TabsContent>
              )
            })}
          </Tabs>
        </>
      )}
    </div>
  )
}
